import React, { useState, useEffect, useCallback } from "react";
import { Button, Card, Alert, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { deleteApplication, recover, getApplication, getlogurl } from "../api/APIUtils";
import ProgressBar from "./ProgressBar";
import _ from "lodash";
import AlertDialog from "./Dialog.js";
import ConfirmModal from "./ConfirmModal";
import TagsDisplay from "./TagsDisplay";
import VMStatusModal from "./VMStatusModal";

const App = ({
  id,
  name,
  entity,
  plan,
  date,
  updatedAt,
  nonExisting,
  downgraded_products,
  unhealthy_products,
  replaced_products,
  failed_instances,
  instances,
  reload,
  deleteApp,
  dr_status,
  instances_recovered,
  dr_state,
  handleError,
  isAdmin,
  isOperator,
  primary_az_id,
  secondary_az_id,
  hostname,
  tags
}) => {
  const history = useHistory();
  const [completed, setCompleted] = useState(instances_recovered);
  const [status, setStatus] = useState(dr_status);
  const [drState, setDrState] = useState(dr_state);
  const [currentFailedInstances, setCurrentFailedInstances] = useState(failed_instances);
  const [currentInstances, setCurrentInstances] = useState(instances);
  const [visible, setVisible] = useState(false);
  const [message, setMessage] = useState("");
  const [info, setInfo] = useState("");
  const [isLoading, setLoading] = useState(false);
  const [enableFallback, setEnableFallback] = useState(false);
  const [enableFailover, setEnableFailover] = useState(false);
  const [enableDeleteApp, setEnableDeleteApp] = useState(false);

  // Poll DynamoDB via GraphQL query while execution is in progress
  useEffect(() => {
    const isRunning = status === 'In_Progress';
    if (!isRunning) return;

    const interval = setInterval(() => {
      getApplication(id)
        .then((response) => {
          const app = response.data.getApplication;
          setStatus(app.dr_status);
          setCompleted(app.instances_recovered);
          setDrState(app.dr_state);
          setCurrentFailedInstances(app.failed_instances);
          setCurrentInstances(app.instances);
        })
        .catch((e) => console.error('Polling error:', e));
    }, 10000);

    return () => clearInterval(interval);
  }, [id, status]);

  const removeApp = (id, reload) => {
    deleteApplication({ id })
      .then(() => {
        deleteApp(id);
      })
      .catch((e) => {
        const err = _.get(e, "errors");
        const error = _.find(err, "message");
        console.log(error);
        alert(error.message);
      });
  };

  const disableToggle = (isOperator, isAdmin) => {
    if (!isAdmin && !isOperator){ // both ops and admins are allowed to perform actions.
      return true
    } else {
      return false
    }
  }

  const handleToggle = () => {
    setVisible(false);
    setLoading(true);
  };
  const handleRe = useCallback(() => {
    setVisible((c) => c === false);
    setLoading((c) => c === false);
  }, []);
  useEffect(() => {
    if (isLoading) {
      getApplication(id)
        .then((response) => {
          setDrState(response.data.getApplication.dr_state);
          setStatus(response.data.getApplication.dr_status);
          setCompleted(response.data.getApplication.instances_recovered);
          setCurrentFailedInstances(response.data.getApplication.failed_instances);
          setCurrentInstances(response.data.getApplication.instances);
          // handleError("This where the error would be displayed if the step function fails..")
          setInfo("success");
          setMessage("Updated successfully!");
          handleRe();
        })
        .catch((e) => {
          setInfo("danger");
          setMessage("Something Went Wrong!");
          handleRe();
          console.log(e);
        })
        .finally(() => {
          // handleRe();
          setTimeout(() => {
            setVisible(false);
          }, 5000);
        });
    }
    return () => {
      clearTimeout();
    };
  }, [isLoading, id, handleRe]);

  return (
    <>
      <div style={{ position: "absolute", top: 0, left: 0 }}>
        {visible && (
          <Alert variant={info} onClose={() => setVisible(false)} dismissible>
            <p>{message}</p>
          </Alert>
        )}
      </div>
      <div>
        <Card style={{ width: "24.8rem" }} className="app">
          <Card.Body>
            <Card.Title className="app-title">{name}</Card.Title>
            <TagsDisplay tags={tags} />
            <div className="App-details">
              <div>State: {drState} </div>
              <div>Status: <VMStatusModal status={status} failed_instances={currentFailedInstances} instances={currentInstances} nonExisting={nonExisting} /> </div>
              {primary_az_id && <div>Primary: {primary_az_id} </div>}
              {(!secondary_az_id || !primary_az_id) && (
                <div>
                  <b>Please save your plan again</b>
                </div>
              )}
              <ProgressBar bgcolor={"#6a1b9a"} completed={completed} />
              {status?.includes('Failed') && status !== 'In_Progress' && (drState == "primary") && (
                <ConfirmModal disabled={disableToggle(isOperator, isAdmin)} variant="primary"  action="Retry Failover" name={name} onConfirm = {()=> recover(id, name, entity, plan, "secondary")}/>
              )}
              {status?.includes('Failed') && status !== 'In_Progress' && (drState == "secondary") && (
                <ConfirmModal disabled={disableToggle(isOperator, isAdmin)} variant="primary"  action="Retry Fallback" name={name} onConfirm = {()=> recover(id, name, entity, plan, "primary")}/>
              )}
              <div>Date(create): {new Date(date).toDateString()}</div>
              <div>Date(update): {new Date(updatedAt).toDateString()}</div>
              {nonExisting && <div class="alert alert-danger">Product ID(s) not available: {nonExisting} </div>}
              {downgraded_products && <div class="alert alert-warning">Product ID(s) are downgraded: {downgraded_products} and as a result are skipped. </div>}
              {unhealthy_products && <div class="alert alert-info">Product ID(s) have unhealthy VMs: {unhealthy_products} </div>}
              {replaced_products && <div class="alert alert-info">Hostnames have updated new product ids: {replaced_products} </div>}
            </div>
              <span className="d-inline-block">  
              <ConfirmModal disabled={disableToggle(isOperator, isAdmin)} variant="success" action="Failover" name={name} onConfirm = {()=> recover(id, name, entity, plan, "secondary")}/>              
              <ConfirmModal disabled={disableToggle(isOperator, isAdmin)} variant="primary" action="Fallback" name={name} onConfirm = {()=> recover(id, name, entity, plan, "primary")}/>
              </span>

            <Button
              variant="secondary"
              onClick={() => history.push(`/edit/${id}`)}
              // disabled={!isAdmin}
            >
              {(isAdmin || isOperator) ? "Edit" : "View"}
            </Button>{" "}
            <Button
              variant="secondary"
              onClick={() => getlogurl(id)}
            >
              Log
            </Button>

            <ConfirmModal variant="danger"  action="Delete" onConfirm = {()=>{removeApp(id, reload);history.push("/");}} disabled={!isAdmin}/>

          </Card.Body>

          <div align="right">
            <Button
              variant="info"
              size="sm"
              onClick={!isLoading ? handleToggle : null}
              disabled={isLoading || disableToggle(isOperator, isAdmin)}
            >
              {isLoading ? "Updating…" : "Update"}
            </Button>{" "}
          </div>
        </Card>
      </div>
    </>
  );
};
export default App;
